import { c as _c } from "react/compiler-runtime";
import { Box, Stack } from "@mui-wrapper";
import { pickersLayoutClasses, PickersLayoutContentWrapper, usePickerContext, usePickerLayout } from "@mui/x-date-pickers-pro";
import { Checkbox, FontWeights, NO_TRANSLATE_CLASS, Text } from "@flat-ui";
import { formatRangeHeader } from "@components/date-range-picker/date-range-picker-utils";
import { PALETTE_VARS } from "@styles/palette-vars";
/**
 * Custom layout used by the DateRangePicker popper.
 * It renders the title, shortcuts, selected range header, calendar content,
 * optional compare checkbox, and action bar with FARO styling.
 *
 * @returns The styled date-range calendar layout for the picker popper.
 */
export function CustomRangeCalendarLayout(t0) {
  const $ = _c(98);
  let compareLabel;
  let isCompareChecked;
  let isDark;
  let onCompareChange;
  let pickerTitle;
  let rest;
  let shouldEnableCompare;
  let variantStyle;
  if ($[0] !== t0) {
    ({
      variantStyle,
      isDark,
      pickerTitle,
      shouldEnableCompare,
      compareLabel,
      isCompareChecked,
      onCompareChange,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = compareLabel;
    $[2] = isCompareChecked;
    $[3] = isDark;
    $[4] = onCompareChange;
    $[5] = pickerTitle;
    $[6] = rest;
    $[7] = shouldEnableCompare;
    $[8] = variantStyle;
  } else {
    compareLabel = $[1];
    isCompareChecked = $[2];
    isDark = $[3];
    onCompareChange = $[4];
    pickerTitle = $[5];
    rest = $[6];
    shouldEnableCompare = $[7];
    variantStyle = $[8];
  }
  const {
    shortcuts,
    content,
    actionBar,
    ownerState
  } = usePickerLayout(rest);
  const {
    value
  } = usePickerContext();
  let t1;
  if ($[9] !== value) {
    t1 = value ?? [null, null];
    $[9] = value;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = formatRangeHeader(t1);
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const rangeHeader = t2;
  const hasSelectedRange = Boolean(rangeHeader);
  const t3 = `1px solid ${variantStyle.dividerColor}`;
  let t4;
  if ($[13] !== t3 || $[14] !== variantStyle.backgroundColor) {
    t4 = {
      backgroundColor: variantStyle.backgroundColor,
      borderRadius: "0.25rem",
      border: t3
    };
    $[13] = t3;
    $[14] = variantStyle.backgroundColor;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== pickerTitle || $[17] !== variantStyle.secondaryTextColor) {
    t5 = pickerTitle && <Box sx={{
      px: 3,
      pt: 2,
      pb: 0
    }}><Text variant="bodyM" color={variantStyle.secondaryTextColor}>{pickerTitle}</Text></Box>;
    $[16] = pickerTitle;
    $[17] = variantStyle.secondaryTextColor;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== shortcuts || $[20] !== variantStyle.backgroundColor || $[21] !== variantStyle.hoverBackgroundColor || $[22] !== variantStyle.secondaryTextColor || $[23] !== variantStyle.selectedColor) {
    t6 = shortcuts && <Box className={NO_TRANSLATE_CLASS} sx={{
      px: 3,
      pt: 1.5,
      boxSizing: "border-box",
      width: "19rem",
      maxWidth: "100%",
      "& .MuiList-root": {
        display: "flex",
        flexWrap: "wrap",
        width: "100%",
        maxWidth: "none !important",
        gap: 1.5,
        p: 0
      },
      "& .MuiListItem-root": {
        width: "auto",
        flex: "0 0 auto",
        p: 0
      },
      "& .MuiChip-root": {
        width: "auto",
        height: "auto",
        backgroundColor: variantStyle.backgroundColor,
        color: variantStyle.secondaryTextColor,
        border: `1px solid ${PALETTE_VARS.divider}`,
        "&:hover": {
          backgroundColor: `${variantStyle.hoverBackgroundColor}26`
        },
        "&.DateRangePicker-shortcutActive": {
          backgroundColor: `${variantStyle.selectedColor}14`,
          color: variantStyle.selectedColor,
          border: `1px solid ${variantStyle.selectedColor}`,
          fontWeight: FontWeights.SemiBold
        },
        "&.DateRangePicker-shortcutActive:hover": {
          backgroundColor: `${variantStyle.selectedColor}24`
        }
      },
      "& .MuiChip-label": {
        padding: "5px 8px",
        textTransform: "none",
        whiteSpace: "nowrap"
      }
    }}>{shortcuts}</Box>;
    $[19] = shortcuts;
    $[20] = variantStyle.backgroundColor;
    $[21] = variantStyle.hoverBackgroundColor;
    $[22] = variantStyle.secondaryTextColor;
    $[23] = variantStyle.selectedColor;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const t7 = hasSelectedRange ? "visible" : "hidden";
  let t8;
  if ($[25] !== t7) {
    t8 = {
      px: 3,
      pt: 1.5,
      minHeight: "2.125rem",
      visibility: t7
    };
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = rangeHeader?.start;
  let t10;
  if ($[27] !== t9) {
    t10 = <Text variant="heading20">{t9}</Text>;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const t11 = rangeHeader?.end;
  let t12;
  if ($[29] !== t11 || $[30] !== variantStyle.secondaryTextColor) {
    t12 = <Text variant="heading20" color={variantStyle.secondaryTextColor}>{t11}</Text>;
    $[29] = t11;
    $[30] = variantStyle.secondaryTextColor;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t12 || $[34] !== t8) {
    t13 = <Box sx={t8}>{t10}{" - "}{t12}</Box>;
    $[32] = t10;
    $[33] = t12;
    $[34] = t8;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  let t15;
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      minHeight: "unset",
      height: "auto",
      display: "flex",
      flexDirection: "column"
    };
    t15 = {
      height: "auto"
    };
    t16 = {
      position: "relative !important"
    };
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t14 = $[36];
    t15 = $[37];
    t16 = $[38];
  }
  let t17;
  if ($[39] !== variantStyle.secondaryColor) {
    t17 = {
      color: variantStyle.secondaryColor,
      fontSize: "0.75rem"
    };
    $[39] = variantStyle.secondaryColor;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[41] !== variantStyle.secondaryTextColor) {
    t18 = {
      color: variantStyle.secondaryTextColor
    };
    t19 = {
      color: variantStyle.secondaryTextColor
    };
    t20 = {
      color: variantStyle.secondaryTextColor,
      fontSize: "1.5rem"
    };
    t21 = {
      color: variantStyle.secondaryTextColor,
      fontWeight: FontWeights.SemiBold,
      fontSize: "0.875rem"
    };
    $[41] = variantStyle.secondaryTextColor;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t18 = $[42];
    t19 = $[43];
    t20 = $[44];
    t21 = $[45];
  }
  const t22 = `${variantStyle.selectedColor}1A`;
  let t23;
  if ($[46] !== t22) {
    t23 = {
      backgroundColor: t22
    };
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  const t24 = `${variantStyle.selectedColor}1A`;
  let t25;
  if ($[48] !== t24) {
    t25 = {
      borderColor: t24
    };
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  const t26 = `${variantStyle.hoverBackgroundColor}26`;
  let t27;
  if ($[50] !== t26 || $[51] !== variantStyle.selectedColor) {
    t27 = {
      backgroundColor: t26,
      color: variantStyle.selectedColor
    };
    $[50] = t26;
    $[51] = variantStyle.selectedColor;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== variantStyle.selectedColor) {
    t28 = {
      backgroundColor: variantStyle.selectedColor
    };
    $[53] = variantStyle.selectedColor;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t28 || $[56] !== variantStyle.selectedColor || $[57] !== variantStyle.textColorSelected) {
    t29 = {
      backgroundColor: variantStyle.selectedColor,
      color: variantStyle.textColorSelected,
      fontWeight: FontWeights.SemiBold,
      "&:hover, &:focus": t28
    };
    $[55] = t28;
    $[56] = variantStyle.selectedColor;
    $[57] = variantStyle.textColorSelected;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== variantStyle.disabledSecondaryTextColor) {
    t30 = {
      color: variantStyle.disabledSecondaryTextColor
    };
    $[59] = variantStyle.disabledSecondaryTextColor;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== t27 || $[62] !== t29 || $[63] !== t30 || $[64] !== variantStyle.secondaryColor) {
    t31 = {
      color: variantStyle.secondaryColor,
      fontSize: "0.875rem",
      fontWeight: FontWeights.Regular,
      "&:hover": t27,
      "&.Mui-selected": t29,
      "&.MuiPickersDay-dayOutsideMonth": t30
    };
    $[61] = t27;
    $[62] = t29;
    $[63] = t30;
    $[64] = variantStyle.secondaryColor;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t17 || $[67] !== t18 || $[68] !== t19 || $[69] !== t20 || $[70] !== t21 || $[71] !== t23 || $[72] !== t25 || $[73] !== t31) {
    t32 = {
      "& .MuiPickersSlideTransition-root": t14,
      "& .MuiDayCalendar-root": t15,
      "& .MuiDayCalendar-monthContainer": t16,
      "& .MuiPickersCalendarHeader-label": t17,
      "& .MuiPickersCalendarHeader-switchViewButton": t18,
      "& .MuiPickersArrowSwitcher-button": t19,
      "& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root": t20,
      "& .MuiDayCalendar-weekDayLabel": t21,
      "& .MuiDateRangePickerDay-rangeIntervalDayHighlight": t23,
      "& .MuiDateRangePickerDay-rangeIntervalDayPreview": t25,
      "& .MuiDateRangePickerDay-day": t31
    };
    $[66] = t17;
    $[67] = t18;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
    $[71] = t23;
    $[72] = t25;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== content || $[76] !== ownerState) {
    t33 = <PickersLayoutContentWrapper ownerState={ownerState} className={pickersLayoutClasses.contentWrapper}>{content}</PickersLayoutContentWrapper>;
    $[75] = content;
    $[76] = ownerState;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t32 || $[79] !== t33) {
    t34 = <Box className={NO_TRANSLATE_CLASS} sx={t32}>{t33}</Box>;
    $[78] = t32;
    $[79] = t33;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== compareLabel || $[82] !== isCompareChecked || $[83] !== isDark || $[84] !== onCompareChange || $[85] !== shouldEnableCompare) {
    t35 = shouldEnableCompare && <Checkbox dark={isDark} checked={isCompareChecked} onChange={event => onCompareChange?.(event.target.checked)} label={compareLabel} controlSx={{
      mx: 1.5,
      mt: 1.25
    }} />;
    $[81] = compareLabel;
    $[82] = isCompareChecked;
    $[83] = isDark;
    $[84] = onCompareChange;
    $[85] = shouldEnableCompare;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      "& .MuiDialogActions-root": {
        px: 1.5
      }
    };
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== actionBar) {
    t37 = <Box sx={t36}>{actionBar}</Box>;
    $[88] = actionBar;
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  let t38;
  if ($[90] !== t13 || $[91] !== t34 || $[92] !== t35 || $[93] !== t37 || $[94] !== t4 || $[95] !== t5 || $[96] !== t6) {
    t38 = <Stack sx={t4}>{t5}{t6}{t13}{t34}{t35}{t37}</Stack>;
    $[90] = t13;
    $[91] = t34;
    $[92] = t35;
    $[93] = t37;
    $[94] = t4;
    $[95] = t5;
    $[96] = t6;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  return t38;
}